// per specs/060-agreed-alerts/plan.md C2 (History), C4 (GET /:id/history), tasks T022
// Every change of one watched group, newest first, with author and time and the before and
// after of each field (caiet TF-127: "istoric complet al modificărilor"). The cells are the
// catalog history log's own (CatalogHistoryLog.tsx), so a change reads the same here as on the
// nomenclator history it follows (NomenclatorHistoryPage.tsx).
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn } from '@egov-moldova/mud/loader';
import { MudTable } from '@egov-moldova/mud-react';
import {
  Breadcrumb,
  EmptyRegisterMessage,
  formatDate,
  formatTime,
  PageHeader,
} from '@simsm/ui-shared';
import { loadFailureKind } from '../../app/api-client';
import { LOAD_ERROR_KEYS } from '../../components/FormPage/load-error-copy';
import { QueryState } from '../../components/QueryState/QueryState';
import { HistoryChanges } from '../../features/history/HistoryChanges';
import { catalogAttribution, HistoryActor } from '../../features/history/CatalogHistoryLog';
import { HistoryMeta } from '../../features/history/HistoryMeta';
import { fetchWatchedGroupHistory, getWatchedGroup } from './watched-groups-api';

export function WatchedGroupHistoryPage(): JSX.Element {
  const { t } = useTranslate();
  const { id = '' } = useParams<{ id: string }>();

  const group = useQuery({ queryKey: ['watched-group', id], queryFn: () => getWatchedGroup(id) });
  const history = useQuery({
    queryKey: ['watched-group-history', id],
    queryFn: () => fetchWatchedGroupHistory(id),
  });
  const entries = history.data ?? [];
  const groupName = group.data?.name ?? '';

  // A group that is gone says so; any other failure of the read is the register's own error.
  if (group.isError && loadFailureKind(group.error) === 'not_found') {
    return (
      <section className="simsm-page simsm-history">
        <p>{t(LOAD_ERROR_KEYS.not_found)}</p>
      </section>
    );
  }

  const columns: TableColumn[] = [
    { key: 'recordedAt', label: t('history.column.when'), width: '10rem' },
    { key: 'change', label: t('history.column.change') },
    { key: 'actor', label: t('history.column.actor'), width: '10rem' },
  ];

  const register = (
    <div className="simsm-history__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={entries.map((entry) => ({ id: entry.id }))}
        rowStyle="zebra"
        aria-label={t('watched_groups.history.title')}
      >
        <EmptyRegisterMessage>{t('history.empty')}</EmptyRegisterMessage>
        {entries.map((entry, index) => [
          <span
            key={`when-${entry.id}`}
            slot={`cell-recordedAt-${index}`}
            className="simsm-history__cell"
          >
            <span>{formatDate(entry.recordedAtIso)}</span>
            <HistoryMeta
              label={t('history.meta.label.time')}
              value={formatTime(entry.recordedAtIso)}
            />
          </span>,
          <span
            key={`change-${entry.id}`}
            slot={`cell-change-${index}`}
            className="simsm-history__cell simsm-history__cell--change"
          >
            <HistoryChanges entry={entry} />
          </span>,
          <span
            key={`actor-${entry.id}`}
            slot={`cell-actor-${index}`}
            className="simsm-history__cell"
          >
            <HistoryActor entry={catalogAttribution(entry)} />
          </span>,
        ])}
      </MudTable>
    </div>
  );

  return (
    <section className="simsm-page simsm-history">
      <div className="simsm-history__breadcrumb">
        <Breadcrumb
          maxVisible={4}
          label={t('common.breadcrumb.aria_label')}
          crumbs={[
            { label: t('alerts.section_title'), to: '/system-admin/alerts' },
            { label: t('alerts.section.watched_groups'), to: '/system-admin/watched-groups' },
            ...(groupName
              ? [{ label: groupName, to: `/system-admin/watched-groups/${id}/edit` }]
              : []),
            { label: t('watched_groups.history.crumb') },
          ]}
        />
      </div>

      <PageHeader
        title={t('watched_groups.history.title')}
        lede={
          groupName
            ? t('watched_groups.history.lede', { name: groupName })
            : t('watched_groups.history.lede_unnamed')
        }
      />

      <QueryState query={history} isEmpty={entries.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}
